import { call, put, select, take, fork } from 'redux-saga/effects';
import { frameSuccess, frameFailure, frameRequest, SeekAction, AssignStreamAction } from 'actions/monitoringActions';
import { getCell, getStreamPosition, isPlaying } from 'selectors/monitoringSelectors';
import { splitStreamId } from 'util/streamId';
import { CellState, CellIndex, SeekType, UtcTime, StreamId } from 'types';
import { handleErrorSaga } from '../app/handleError';
import { postSeekRequest, SeekStreamResponse } from 'api/camera';

export function* assignStreamWatcher() {
    while (true) {
        // Wait for a camera stream to be assigned to a cell
        const action: AssignStreamAction = yield take('ASSIGN_STREAM');
        const { cellIndex, streamId } = action.payload;
        if (!streamId) continue;

        // Ignore cells that are playing video (VideoView will take care of switching to the new camera)
        const playing = yield select(isPlaying, { cellIndex });
        if (playing) continue;

        // Start a task to fetch the still frame for this cell (at the current time in the cell)
        const utcTime = yield select(getStreamPosition, { cellIndex });
        yield fork(seekSaga, cellIndex, streamId, 'to', utcTime);
    }
}

export function* seekWatcher() {
    while (true) {
        const action: SeekAction = yield take('SEEK');
        const { cellIndex, seekType = 'to' } = action.payload;
        const { streamId }: CellState = yield select(getCell, { cellIndex });
        if (!streamId) {
            // Shouldn't have seek commands if a cell does not have a stream assigned!
            console.error(`Expected a valid streamId in cell ${cellIndex}`);
            continue;
        }
        
        // If a seek time wasn't specified, look up the stream position of the cell
        let { utcTime } = action.payload;
        if (typeof utcTime === 'undefined') {
            utcTime = yield select(getStreamPosition, { cellIndex });
        }

        yield fork(seekSaga, cellIndex, streamId, seekType, utcTime!);
    }
}

export function* seekSaga(cellIndex: CellIndex, streamId: StreamId, seekType: SeekType, utcTime: UtcTime) {
    let frame: string;
    const { deviceId, streamIndex } = splitStreamId(streamId);

    yield put(frameRequest(cellIndex));
    try {
        const result: SeekStreamResponse = yield call(postSeekRequest, deviceId, streamIndex, seekType, utcTime, true);
        utcTime = result.utcTime;
        frame = result.frame;
    }
    catch (err) {
        yield call(handleErrorSaga, err, frameFailure, { cellIndex });
        return;
    }
    yield put(frameSuccess(frame, { cellIndex, utcTime }));
}



// WEBPACK FOOTER //
// ./src/sagas/monitoring/seekSaga.ts